import { useEffect, useState } from "react";

// Fallback only: a bundle served by something other than server-app.js still reports
// something. The deployed version comes from GET /__version at run time.
const buildTimeVersion =
  (import.meta.env.VITE_APP_VERSION as string | undefined) || "dev";

export interface AppVersionInfo {
  frontend: string;
  backend: string | null;
}

let frontendVersionPromise: Promise<string | null> | null = null;
let backendVersionPromise: Promise<string | null> | null = null;

function fetchFrontendVersion(): Promise<string | null> {
  if (frontendVersionPromise) return frontendVersionPromise;
  // The SPA fallback answers unknown paths with index.html, so an older image without
  // the route lands in the catch and keeps the build-time value.
  frontendVersionPromise = fetch("/__version")
    .then((r) => r.json())
    .then((data: { version?: string }) => data?.version ?? null)
    .catch(() => null);
  return frontendVersionPromise;
}

function fetchBackendVersion(): Promise<string | null> {
  if (backendVersionPromise) return backendVersionPromise;
  // Same rule as api.ts: in production the API is same-origin, behind nginx's /api.
  const base =
    (import.meta.env.VITE_API_URL as string) || "http://localhost:2222";
  const healthUrl = import.meta.env.PROD
    ? "/api/healthz"
    : `${base.replace(/\/$/, "")}/healthz`;
  backendVersionPromise = fetch(healthUrl, { credentials: "include" })
    .then((r) => r.json())
    .then((data: { version?: string }) => data?.version ?? null)
    .catch(() => null);
  return backendVersionPromise;
}

/**
 * Exposes frontend and backend versions, both fetched at run time.
 * Sets data attributes on <html> for browser inspector:
 *   data-mage-x-frontend-version, data-mage-x-backend-version
 */
export function useAppVersion(): AppVersionInfo {
  const [frontendVersion, setFrontendVersion] =
    useState<string>(buildTimeVersion);
  const [backendVersion, setBackendVersion] = useState<string | null>(null);

  useEffect(() => {
    const el = document.documentElement;
    el.dataset.mageXFrontendVersion = buildTimeVersion;

    fetchFrontendVersion().then((v) => {
      if (!v) return;
      setFrontendVersion(v);
      el.dataset.mageXFrontendVersion = v;
    });

    fetchBackendVersion().then((v) => {
      setBackendVersion(v);
      if (v) el.dataset.mageXBackendVersion = v;
    });
  }, []);

  return { frontend: frontendVersion, backend: backendVersion };
}
